/**
 * Stamp Duty Calculator Block (UK)
 * Calculates UK stamp duty based on property price and buyer type.
 */

import { registerBlockRenderer, getBlockRendererContext } from '../registry';
import { escapeHtml, createBlockWrapper } from '../utils';

// =============================================================================
// TYPES
// =============================================================================

type BuyerType = 'first-time' | 'home-mover' | 'additional';

interface StampDutyData {
  propertyPrice?: number;
  buyerType?: BuyerType;
  title?: string;
  currency?: string;
}

// =============================================================================
// TYPE VALIDATION
// =============================================================================

function isValidStampDutyData(data: unknown): data is StampDutyData {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;

  if (d.propertyPrice !== undefined && typeof d.propertyPrice !== 'number') return false;
  if (d.title !== undefined && typeof d.title !== 'string') return false;
  if (d.buyerType !== undefined) {
    if (!['first-time', 'home-mover', 'additional'].includes(d.buyerType as string)) return false;
  }

  return true;
}

// =============================================================================
// STAMP DUTY CALCULATION
// =============================================================================

interface TaxBand {
  threshold: number;
  rate: number;
}

function getStampDutyBands(buyerType: BuyerType, price: number): TaxBand[] {
  if (buyerType === 'first-time' && price <= 625000) {
    // First-time buyer relief
    return [
      { threshold: 425000, rate: 0 },
      { threshold: 625000, rate: 0.05 },
    ];
  } else if (buyerType === 'additional') {
    // Additional property surcharge
    return [
      { threshold: 250000, rate: 0.03 },
      { threshold: 925000, rate: 0.08 },
      { threshold: 1500000, rate: 0.13 },
      { threshold: Infinity, rate: 0.15 },
    ];
  } else {
    // Standard rates
    return [
      { threshold: 250000, rate: 0 },
      { threshold: 925000, rate: 0.05 },
      { threshold: 1500000, rate: 0.10 },
      { threshold: Infinity, rate: 0.12 },
    ];
  }
}

function calculateStampDuty(price: number, buyerType: BuyerType): { total: number; rate: number; breakdown: string[] } {
  const bands = getStampDutyBands(buyerType, price);

  let totalDuty = 0;
  let previousThreshold = 0;
  const breakdown: string[] = [];

  for (const band of bands) {
    if (price > previousThreshold) {
      const taxableAmount = Math.min(price, band.threshold) - previousThreshold;
      const bandDuty = taxableAmount * band.rate;
      if (bandDuty > 0) {
        totalDuty += bandDuty;
        breakdown.push(
          `£${previousThreshold.toLocaleString()} - £${Math.min(price, band.threshold).toLocaleString()} @ ${(band.rate * 100).toFixed(0)}% = £${Math.round(bandDuty).toLocaleString()}`
        );
      }
      previousThreshold = band.threshold;
    }
  }

  const effectiveRate = price > 0 ? (totalDuty / price) * 100 : 0;

  return { total: totalDuty, rate: effectiveRate, breakdown };
}

// =============================================================================
// RENDER FUNCTION
// =============================================================================

function renderStampDuty(data: StampDutyData, container: HTMLElement): void {
  const wrapper = createBlockWrapper('stamp-duty');

  // Get currency from data, fall back to context, then to £
  const context = getBlockRendererContext();
  const currency = data.currency || context.currencySymbol || '£';
  const defaultPrice = data.propertyPrice || 425000;
  const defaultBuyerType = data.buyerType || 'home-mover';

  let html = `<h3 class="pms-stamp-duty-title">${escapeHtml(data.title || 'Stamp Duty Calculator')}</h3>`;
  html += '<div class="pms-stamp-duty">';

  // Buyer Type Selection
  html += `
    <div class="pms-stamp-duty-buyer-types">
      <button class="pms-stamp-duty-buyer-btn${defaultBuyerType === 'first-time' ? ' pms-active' : ''}" data-type="first-time">
        First-time Buyer
      </button>
      <button class="pms-stamp-duty-buyer-btn${defaultBuyerType === 'home-mover' ? ' pms-active' : ''}" data-type="home-mover">
        Home Mover
      </button>
      <button class="pms-stamp-duty-buyer-btn${defaultBuyerType === 'additional' ? ' pms-active' : ''}" data-type="additional">
        Additional Property
      </button>
    </div>
  `;

  // Property Price
  html += `
    <div class="pms-stamp-duty-field">
      <label class="pms-stamp-duty-label">Property Price</label>
      <div class="pms-stamp-duty-input-group">
        <span class="pms-stamp-duty-currency">${escapeHtml(currency)}</span>
        <input type="number" class="pms-stamp-duty-input" data-field="price" value="${defaultPrice}" min="0" max="10000000" step="1000">
      </div>
    </div>
  `;

  // Results
  html += `
    <div class="pms-stamp-duty-results">
      <div class="pms-stamp-duty-result-main">
        <span class="pms-stamp-duty-result-label">Stamp Duty to Pay</span>
        <span class="pms-stamp-duty-result-value" data-display="total">${currency}0</span>
      </div>
      <div class="pms-stamp-duty-rate" data-display="rate">Effective rate: 0%</div>
      <div class="pms-stamp-duty-breakdown" data-display="breakdown"></div>
    </div>
  `;

  html += '</div>';
  wrapper.innerHTML = html;

  // Get elements
  const priceInput = wrapper.querySelector('[data-field="price"]') as HTMLInputElement;
  const totalDisplay = wrapper.querySelector('[data-display="total"]') as HTMLElement;
  const rateDisplay = wrapper.querySelector('[data-display="rate"]') as HTMLElement;
  const breakdownDisplay = wrapper.querySelector('[data-display="breakdown"]') as HTMLElement;
  const buyerBtns = wrapper.querySelectorAll('.pms-stamp-duty-buyer-btn');

  let currentBuyerType: BuyerType = defaultBuyerType;

  function update(): void {
    const price = parseFloat(priceInput.value) || 0;
    const result = calculateStampDuty(price, currentBuyerType);

    totalDisplay.textContent = `${currency}${Math.round(result.total).toLocaleString()}`;
    rateDisplay.textContent = `Effective rate: ${result.rate.toFixed(2)}%`;

    if (result.breakdown.length > 0) {
      breakdownDisplay.innerHTML = `
        <button class="pms-stamp-duty-breakdown-toggle" data-expanded="false">
          Show breakdown
        </button>
        <div class="pms-stamp-duty-breakdown-content" style="display: none;">
          ${result.breakdown.map(b => `<div class="pms-stamp-duty-breakdown-item">${b}</div>`).join('')}
        </div>
      `;

      const toggleBtn = breakdownDisplay.querySelector('.pms-stamp-duty-breakdown-toggle');
      const content = breakdownDisplay.querySelector('.pms-stamp-duty-breakdown-content') as HTMLElement;
      toggleBtn?.addEventListener('click', () => {
        const expanded = toggleBtn.getAttribute('data-expanded') === 'true';
        toggleBtn.setAttribute('data-expanded', String(!expanded));
        toggleBtn.textContent = expanded ? 'Show breakdown' : 'Hide breakdown';
        content.style.display = expanded ? 'none' : 'block';
      });
    } else {
      breakdownDisplay.innerHTML = '';
    }
  }

  // Event listeners
  priceInput.addEventListener('input', update);

  buyerBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      buyerBtns.forEach(b => b.classList.remove('pms-active'));
      btn.classList.add('pms-active');
      currentBuyerType = (btn as HTMLElement).dataset.type as BuyerType;
      update();
    });
  });

  // Initial calculation
  update();

  container.appendChild(wrapper);
}

// =============================================================================
// REGISTRATION
// =============================================================================

registerBlockRenderer('stamp-duty', (data: unknown, container: HTMLElement) => {
  if (!isValidStampDutyData(data)) {
    console.warn('[stamp-duty] Invalid data:', data);
    const wrapper = createBlockWrapper('stamp-duty');
    wrapper.innerHTML = '<div class="pms-block-error">Invalid stamp duty calculator data</div>';
    container.appendChild(wrapper);
    return;
  }

  renderStampDuty(data, container);
});
